// hooks/useUsage.js

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

export const PLANS = {
  free:    { label: 'Free',    leadsLimit: 10,   price: '$0',      priceNum: 0   },
  starter: { label: 'Starter', leadsLimit: 300,  price: '$29/mo',  priceNum: 29  },
  pro:     { label: 'Pro',     leadsLimit: 1000, price: '$79/mo',  priceNum: 79  },
  agency:  { label: 'Agency',  leadsLimit: 3000, price: '$199/mo', priceNum: 199 },
};

function getNextMonthReset() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1);
}

// Firestore rejects any field whose value is `undefined`.
// This recursively strips undefined from any object/array before a write.
function stripUndefined(obj) {
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return obj;
}

export function useUsage() {
  const { user } = useAuth();
  const uid = user?.uid;

  const [userData,     setUserData]     = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError,   setUsageError]   = useState(null);

  useEffect(() => {
    if (!uid) { setUsageLoading(false); return; }
    const init = async () => {
      try {
        const ref  = doc(db, 'users', uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          const resetDate = getNextMonthReset();
          const newDoc = {
            plan: 'free', leadsUsed: 0, leadsLimit: PLANS.free.leadsLimit,
            resetDate, createdAt: serverTimestamp(), seenIds: [], searches: [],
          };
          await setDoc(ref, newDoc);
          setUserData({ ...newDoc, uid });
        } else {
          const data = snap.data();
          const resetDate = data.resetDate?.toDate?.() ? data.resetDate.toDate() : new Date(data.resetDate);
          if (new Date() >= resetDate) {
            const nextReset = getNextMonthReset();
            await updateDoc(ref, { leadsUsed: 0, resetDate: nextReset });
            setUserData({ ...data, leadsUsed: 0, resetDate: nextReset, uid });
          } else {
            setUserData({ ...data, resetDate, uid });
          }
        }
      } catch (err) {
        console.error('useUsage init error:', err.message);
        setUsageError(err.message);
      } finally {
        setUsageLoading(false);
      }
    };
    init();
  }, [uid]);

  const plan           = userData?.plan      || 'free';
  const leadsUsed      = userData?.leadsUsed || 0;
  const leadsLimit     = PLANS[plan]?.leadsLimit ?? 10;
  const isUnlimited    = false;
  const leadsRemaining = Math.max(0, leadsLimit - leadsUsed);
  const isAtLimit      = leadsUsed >= leadsLimit;
  const seenIds        = userData?.seenIds  || [];
  const searches       = userData?.searches || [];

  const canSearch = (requested = 10) => {
    if (!uid)      return { allowed: false, reason: 'not_logged_in' };
    if (isAtLimit) return { allowed: false, reason: 'limit_reached', available: 0 };
    return { allowed: true, available: Math.min(requested, leadsRemaining) };
  };

  const recordSearch = async (restaurants) => {
    if (!uid || !restaurants?.length) return null;
    try {
      const ref     = doc(db, 'users', uid);
      const newIds  = restaurants.map(r => r.id).filter(id => id && !seenIds.includes(id));
      const newSeen = [...seenIds, ...newIds];
      const count   = restaurants.length;
      const newUsed = Math.min(leadsUsed + count, leadsLimit);
      const entry   = {
        id:        `search_${Date.now()}`,
        type:      'search',
        date:      new Date().toISOString(),
        count,
        label:     `NYC · ${count} leads · ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        restaurants,
        aiChats:   [],
      };
      const newSearches = [entry, ...searches].slice(0, 20);
      await updateDoc(ref, { leadsUsed: newUsed, seenIds: newSeen, searches: newSearches });
      setUserData(prev => ({ ...prev, leadsUsed: newUsed, seenIds: newSeen, searches: newSearches }));
      return { entry, newSearches };
    } catch (err) {
      console.error('useUsage recordSearch error:', err.message);
      return null;
    }
  };

  const recordAIChat = async (parentSearchId, query, aiResult) => {
    if (!uid || !parentSearchId) {
      console.warn('[recordAIChat] missing uid or parentSearchId');
      return null;
    }
    try {
      const ref  = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) { console.warn('[recordAIChat] user doc missing'); return null; }

      const freshSearches = snap.data().searches || [];

      const parentExists = freshSearches.some(s => s.id === parentSearchId);
      if (!parentExists) {
        console.error('[recordAIChat] parentSearchId not found:', parentSearchId);
        return null;
      }

      const modeIcon = { filter: '🔍', analyse: '📊', script: '✉️', email: '✉️', bulk: '📋', off_topic: '💬' };

      // Build chatEntry with only defined, Firestore-safe values.
      // The aiResult object coming from the backend may contain undefined fields
      // (e.g. result.restaurants, result.script, result.analysis depending on mode).
      // Firestore throws on ANY undefined value anywhere in the document,
      // so we run everything through stripUndefined before writing.
      const chatEntry = stripUndefined({
        id:      `ai_${Date.now()}`,
        type:    'ai',
        date:    new Date().toISOString(),
        query:   query || '',
        mode:    aiResult.mode    || 'unknown',
        icon:    modeIcon[aiResult.mode] || '🤖',
        summary: aiResult.summary || '',
        result:  aiResult,
      });

      const newSearches = freshSearches.map(s =>
        s.id === parentSearchId
          ? { ...s, aiChats: [...(s.aiChats || []), chatEntry].slice(-10) }
          : s
      );

      await updateDoc(ref, { searches: newSearches });
      setUserData(prev => ({ ...prev, searches: newSearches }));
      console.log('[recordAIChat] ✓ saved:', chatEntry.id, 'under', parentSearchId);
      return chatEntry;
    } catch (err) {
      console.error('useUsage recordAIChat error:', err.message);
      return null;
    }
  };

  const deleteSearch = async (searchId) => {
    if (!uid) return;
    try {
      const ref         = doc(db, 'users', uid);
      const newSearches = searches.filter(s => s.id !== searchId);
      await updateDoc(ref, { searches: newSearches });
      setUserData(prev => ({ ...prev, searches: newSearches }));
    } catch (err) { console.error('useUsage deleteSearch error:', err.message); }
  };

  const renameSearch = async (searchId, newLabel) => {
    if (!uid || !newLabel?.trim()) return;
    try {
      const ref         = doc(db, 'users', uid);
      const newSearches = searches.map(s => s.id === searchId ? { ...s, label: newLabel.trim() } : s);
      await updateDoc(ref, { searches: newSearches });
      setUserData(prev => ({ ...prev, searches: newSearches }));
    } catch (err) { console.error('useUsage renameSearch error:', err.message); }
  };

  const loadSearch = (searchId) => searches.find(s => s.id === searchId) || null;

  const loadAIChat = (searchId, chatId) => {
    const search = searches.find(s => s.id === searchId);
    if (!search) return null;
    const chat = (search.aiChats || []).find(c => c.id === chatId);
    if (!chat) return null;
    return { restaurants: search.restaurants, aiResult: chat.result, query: chat.query };
  };

  return {
    usageLoading, usageError, userData,
    plan, leadsUsed, leadsLimit, leadsRemaining, isAtLimit, isUnlimited,
    resetDate: userData?.resetDate || null,
    seenIds, searches,
    canSearch, recordSearch, recordAIChat,
    deleteSearch, renameSearch,
    loadSearch, loadAIChat,
  };
}